<template>
  <div class="price-range-table-item-container mb-4">
    <span class="dollar-sign-square" :style="`background-color: ${getRangeColor(index)}`">{{ getDollarCount(index) }}</span>
    <span class="price-range-legend ml-4" :style="`color: ${getRangeColor(index)}`">{{ index }}ª faixa |</span>
    <span class="price-range-legend ml-1" :style="`color: ${getRangeColor(index)}`"
      >{{ minPrice | localeString(2) | currency }}
      -
      {{ maxPrice | localeString(2) | currency }}</span
    >

    <div class="gray-table-container pa-4 mt-2">
      <div class="gray-table-container-headers">
        <div class="legend-item">
          <p class="ma-0">Produtos</p>
          <h6 :style="`color: ${getRangeColor(index)}`">{{ filteredProducts.length }}</h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Média</p>
          <h6 :style="`color: ${getRangeColor(index)}`">{{ priceRange.price_range_metric.percentil_50 | localeString(2) | currency }}</h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Únicos</p>
          <h6 :style="`color: ${getRangeColor(index)}`">
            {{ hasGeneratedPriceRange && priceRange.price_range_metric.unique ? priceRange.price_range_metric.unique : '-' | localeString }}
            <mf-quick-actions v-if="hasGeneratedPriceRange" :icon-color="getRangeColor(index)" v-bind="priceRangeClientsBougthAtRangeQuickAction()" />
          </h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Upsell</p>
          <h6 :style="`color: ${getRangeColor(index)}`">
            {{ hasGeneratedPriceRange && priceRange.price_range_metric.upsell ? priceRange.price_range_metric.upsell : '-' | localeString }}
            <mf-quick-actions v-if="hasGeneratedPriceRange" :icon-color="getRangeColor(index)" v-bind="priceRangeClientsUpsellToRangeQuickAction()" />
          </h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Downsell</p>
          <h6 :style="`color: ${getRangeColor(index)}`">
            {{ hasGeneratedPriceRange && priceRange.price_range_metric.downsell ? priceRange.price_range_metric.downsell : '-' | localeString }}
            <mf-quick-actions v-if="hasGeneratedPriceRange" :icon-color="getRangeColor(index)" v-bind="priceRangeClientsDownsellToRangeQuickAction()" />
          </h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Novos</p>
          <h6 :style="`color: ${getRangeColor(index)}`">
            {{ hasGeneratedPriceRange && priceRange.price_range_metric.new ? priceRange.price_range_metric.new : '-' | localeString }}
            <mf-quick-actions v-if="hasGeneratedPriceRange" :icon-color="getRangeColor(index)" v-bind="priceRangeNewClientAtRangeQuickAction()" />
          </h6>
        </div>

        <div class="legend-item">
          <p class="ma-0">Perdas</p>
          <h6 :style="`color: ${getRangeColor(index)}`">
            {{ hasGeneratedPriceRange && priceRange.price_range_metric.lost ? priceRange.price_range_metric.lost : '-' | localeString }}
            <mf-quick-actions v-if="hasGeneratedPriceRange" :icon-color="getRangeColor(index)" v-bind="priceRangeLostClientAtRangeQuickAction()" />
          </h6>
        </div>
      </div>

      <v-data-table
        :id="`${index}-table`"
        :headers="headers"
        :items="filteredProducts"
        hide-default-footer
        hide-default-header
        disable-pagination
        no-data-text="Não há produtos nesta faixa."
        height="272px"
      >
        <template v-slot:header>
          <tr :class="`thead-${index}`">
            <th v-for="header in headers" :key="header.value" :style="`width: ${header.width}`">
              {{ header.text }}
              <mf-tooltip v-if="header.tooltip" max-width="250" top>
                <template #activator="{ on }">
                  <mf-icon size="20" color="branco">info</mf-icon>
                </template>
                <span v-html="header.tooltip" />
              </mf-tooltip>
            </th>
          </tr>
        </template>
        <template v-slot:item="{ item }">
          <tr v-if="!hasGeneratedPriceRange">
            <td>
              <a target="_blank" class="link" :href="`#/produtos/detalhes-produto/${item.product.sku}`">{{ putEllipsis(item.product.name, 40) }}</a>
            </td>
            <td>
              {{ item.product.price | localeString(2) | currency }}
              <mf-tooltip max-width="250" top>
                <template #activator="{ on }">
                  <mf-icon size="20" color="cinza-0">info</mf-icon>
                </template>
                <span>
                  {{ item.price_range_metric.percentil_20 ? item.price_range_metric.percentil_20 : item.product.price | localeString(2) | currency }} -
                  {{ item.price_range_metric.percentil_80 ? item.price_range_metric.percentil_80 : item.product.price | localeString(2) | currency }}
                </span>
              </mf-tooltip>
            </td>
            <td>
              <v-col cols="5">
                <v-menu bottom origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on }">
                    <div class="select-range" v-on="on">
                      <p class="mb-0">{{ index }}ª faixa <mf-icon>expand_more</mf-icon></p>
                    </div>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="option in selectRangeItems"
                      :key="option.value"
                      :disabled="option.disabled"
                      @click="changeProductRange(option.value, item)"
                    >
                      <v-list-item-title>{{ option.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </td>
          </tr>
          <tr v-else>
            <td>
              <a target="_blank" class="link" :href="`#/produtos/detalhes-produto/${item.product.sku}`">{{ putEllipsis(item.product.name, 40) }}</a>
            </td>
            <td>
              {{ item.product.price | localeString(2) | currency }}
              <mf-tooltip max-width="250" top>
                <template #activator="{ on }">
                  <mf-icon size="20" color="cinza-0">info</mf-icon>
                </template>
                <span>
                  {{ item.price_range_metric.percentil_20 ? item.price_range_metric.percentil_20 : item.product.price | localeString(2) | currency }} -
                  {{ item.price_range_metric.percentil_80 ? item.price_range_metric.percentil_80 : item.product.price | localeString(2) | currency }}
                </span>
              </mf-tooltip>
            </td>
            <td>
              {{ item.price_range_metric.unique || '-' | localeString }}
              <mf-quick-actions :icon-color="getRangeColor(index)" v-bind="priceRangeClientBoughtProductQuickAction(item.product)" />
            </td>
            <td>
              {{ item.price_range_metric.upsell || '-' | localeString }}
              <mf-quick-actions :icon-color="getRangeColor(index)" v-bind="priceRangeClientUpsellByProductQuickAction(item.product)" />
            </td>
            <td>
              {{ item.price_range_metric.downsell || '-' | localeString }}
              <mf-quick-actions :icon-color="getRangeColor(index)" v-bind="priceRangeClientDownsellByProductQuickAction(item.product)" />
            </td>
            <td>
              {{ item.price_range_metric.new || '-' | localeString }}
              <mf-quick-actions :icon-color="getRangeColor(index)" v-bind="priceRangeNewClientByProductQuickAction(item.product)" />
            </td>
            <td>
              {{ item.price_range_metric.lost || '-' | localeString }}
              <mf-quick-actions :icon-color="getRangeColor(index)" v-bind="priceRangeLostClientByProductQuickAction(item.product)" />
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { putEllipsis } from '@/config/helpers.js'
import { DateTime } from 'luxon'
import {
  criteriaGroup,
  priceRangeClientsBougthAtRange,
  priceRangeClientsUpsellToRange,
  priceRangeClientsDownsellToRange,
  priceRangeClientBoughtProduct,
  priceRangeClientUpsellByProduct,
  priceRangeClientDownsellByProduct,
  priceRangeNewClientAtRange,
  priceRangeLostClientAtRange,
  priceRangeNewClientByProduct,
  priceRangeLostClientByProduct,
} from '@/components/quick-actions/segmentationGroups.js'
export default {
  name: 'PriceRangeTableItemContainer',
  inject: ['rangeChanged'],
  components: {},
  props: {
    index: {
      type: Number,
      default: 1,
    },
    priceRanges: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => {},
    },
    rangeCount: {
      type: Number,
      default: 1,
    },
    search: {
      type: String,
      default: '',
    },
    hasGeneratedPriceRange: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    DateTime,
    localUnits: [],
  }),
  computed: {
    ...mapState({
      stateUnits: (state) => state.units,
    }),
    units() {
      return this.stateUnits.units.map((unit) => unit.id_erp)
    },
    unitsConfiguration() {
      const units = this.stateUnits.unitsConfiguration
      return (
        units ||
        this.units.map((unit) => {
          return {
            dataType: ['ECOMMERCE', 'PHYSICAL'],
            value: unit,
          }
        })
      )
    },
    minPrice() {
      return this.priceRange.price_range_metric.min_price || 0
    },
    maxPrice() {
      return this.priceRange.price_range_metric.max_price || 0
    },
    headers() {
      return !this.hasGeneratedPriceRange
        ? [
            {
              text: 'Produtos',
              width: '20%',
              value: 'name',
              tooltip: '',
            },
            {
              text: 'Preço médio',
              width: '20%',
              value: 'price',
              sortable: false,
              tooltip:
                'Mediana de preços praticados no período em análise. Para saber mais informações você pode passar o mouse sobre a mediana de cada produto, dividimos todos os preços em 100 partes, e para não assustar você, estamos mostrando as partes 20 e 80, ou seja, os preços mais baixos e mais altos com maior quantidade de vendas.',
            },
            {
              text: 'Para qual faixa mover este produto',
              sortable: false,
              width: '60%',
              tooltip:
                'Você pode dizer à qual faixa um produto pertence, independente de seu preço. O produto permanecerá na faixa selecionada mesmo que fora do range de preços definida para a faixa. ',
            },
          ]
        : [
            {
              text: 'Produtos',
              width: '20%',
              value: 'name',
              tooltip: '',
            },
            {
              text: 'Preço médio',
              width: '20%',
              value: 'price',
              sortable: false,
              tooltip:
                'Mediana de preços praticados no período em análise. Para saber mais informações você pode passar o mouse sobre a mediana de cada produto, dividimos todos os preços em 100 partes, e para não assustar você, estamos mostrando as partes 20 e 80, ou seja, os preços mais baixos e mais altos com maior quantidade de vendas.',
            },
            {
              text: 'Únicos',
              width: '12%',
              value: 'unique',
              sortable: false,
              tooltip: 'Quantidade de clientes únicos que compraram algum dos produtos da faixa dentro do <b>período atual</b>.',
            },
            {
              text: 'Upsell',
              width: '12%',
              value: 'upsell',
              sortable: false,
              tooltip:
                'Quantidade de clientes únicos que compraram algum dos produtos das faixas inferiores no período referência e compraram nessa faixa no <b>período atual</b>.',
            },
            {
              text: 'Downsell',
              width: '12%',
              value: 'downsell',
              sortable: false,
              tooltip:
                'Quantidade de clientes únicos que compraram algum dos produtos das faixas superiores no período referência e compraram nessa faixa no <b>período atual</b>.',
            },
            {
              text: 'Novos',
              width: '12%',
              value: 'new',
              sortable: false,
              tooltip:
                'Quantidade de clientes que não compraram em nenhuma das faixas no período referência, mas que realizaram a compra de algum dos produtos desta faixa no <b>período atual</b>.',
            },
            {
              text: 'Perdas',
              width: '12%',
              value: 'lost',
              sortable: false,
              tooltip:
                'Quantidade de clientes que compraram nesta faixa de preço no período de referência, <b>mas não realizaram nenhuma nova compra</b>, em nenhuma das faixas, no <b>período atual</b>.',
            },
          ]
    },
    filteredProducts() {
      const products = this.priceRange.products_metrics
      const filtered = this.search ? products.filter((item) => item.product.name.toLowerCase().includes(this.search.toLowerCase())) : products
      return filtered.sort((a, b) => b.product.price - a.product.price)
    },
    selectRangeItems() {
      const array = Array.from(Array(this.rangeCount).keys()).slice().reverse()
      const selectProps = array.map((position) => {
        return {
          label: `${position + 1}ª faixa`,
          value: position + 1,
          disabled: position + 1 === this.index,
        }
      })
      return selectProps
    },
    priceRange() {
      const priceRange = this.priceRanges.find((priceRange) => priceRange.price_range_index === this.index - 1)
      return priceRange
    },
    formatedDateTime() {
      return {
        reference_period_start: DateTime.fromFormat(this.filters.reference_period_start, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
        reference_period_end: DateTime.fromFormat(this.filters.reference_period_end, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
        period_start: DateTime.fromFormat(this.filters.period_start, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
        period_end: DateTime.fromFormat(this.filters.period_end, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
      }
    },
    formatedDateTimeQuickActions() {
      return {
        reference_period_start: DateTime.fromFormat(this.filters.reference_period_start, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        reference_period_end: DateTime.fromFormat(this.filters.reference_period_end, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        period_start: DateTime.fromFormat(this.filters.period_start, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        period_end: DateTime.fromFormat(this.filters.period_end, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
      }
    },
    skusFromCurrentRange() {
      const skus = this.priceRange.products_metrics.map((item) => item.product.sku)
      return skus.length === 0 ? [''] : skus
    },
    skusFromUpperRanges() {
      const upperRanges = this.priceRanges.filter((priceRange) => priceRange.price_range_index > this.index - 1)
      if (!upperRanges || !upperRanges.length) return ['']
      let skus = []
      upperRanges.forEach((range) => {
        range.products_metrics.forEach((item) => {
          skus.push(item.product.sku)
        })
      })
      return skus.length === 0 ? [''] : skus
    },
    skusFromCurrentAndUpperRanges() {
      const skus = [...this.skusFromCurrentRange, ...this.skusFromUpperRanges]
      return skus[0] === [''] && skus[1] === [''] ? [''] : skus
    },
    skusFromLowerRanges() {
      const lowerRanges = this.priceRanges.filter((priceRange) => priceRange.price_range_index < this.index - 1)
      if (!lowerRanges || !lowerRanges.length) return ['']
      let skus = []
      lowerRanges.forEach((range) => {
        range.products_metrics.forEach((item) => {
          skus.push(item.product.sku)
        })
      })
      return skus.length === 0 ? [''] : skus
    },
    skusFromCurrentAndLowerRanges() {
      const skus = [...this.skusFromCurrentRange, ...this.skusFromLowerRanges]
      return skus[0] === [''] && skus[1] === [''] ? [''] : skus
    },
    allSkus() {
      let skus = []
      this.priceRanges.forEach((range) => {
        range.products_metrics.forEach((item) => {
          skus.push(item.product.sku)
        })
      })
      return skus.length === 0 ? [''] : skus
    },
  },
  mounted() {
    this.localUnits = this.stateUnits.units.map((unit) => unit.id_erp)
  },
  methods: {
    putEllipsis,
    getRangeColor(index) {
      switch (index) {
        case 1:
          return '#990066'

        case 2:
          return '#660099'

        case 3:
          return '#190099'

        case 4:
          return '#004d99'

        case 5:
          return '#075A5A'

        case 6:
          return '#0A500A'

        case 7:
          return '#515E00'

        case 8:
          return '#642100'

        case 9:
          return '#6e0000'

        case 10:
          return '#6A4C00'
      }
    },
    getDollarCount(index) {
      let dollars = ''
      for (let i = 1; i <= index; i++) {
        dollars = dollars + '$'
      }
      return dollars
    },
    changeProductRange(index, newItem) {
      const currentPriceRangeItemIndex = this.priceRange.products_metrics.findIndex((item) => item.product.sku === newItem.product.sku)
      this.priceRanges[this.index - 1].products_metrics.splice(currentPriceRangeItemIndex, 1)
      this.priceRanges[index - 1].products_metrics.push(newItem)
      this.rangeChanged()
    },
    priceRangeClientsBougthAtRangeQuickAction() {
      return {
        quickActionGroupId: `clients-bought-at-range${this.index}`,
        shortDescription: `Clientes que compraram na faixa ${this.index}`,
        longDescription: `Clientes únicos que realizaram uma compra na faixa ${this.index} no período ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeClientsBougthAtRange(
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              this.skusFromCurrentRange,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeClientsUpsellToRangeQuickAction() {
      return {
        quickActionGroupId: `clients-upsell-to-range${this.index}`,
        shortDescription: `Clientes que subiram para a faixa ${this.index}`,
        longDescription: `Clientes únicos que realizaram uma compra em alguma faixa inferior no período ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e alguma compra na faixa ${this.index} no período ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeClientsUpsellToRange(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              this.skusFromCurrentRange,
              this.skusFromLowerRanges,
              this.skusFromCurrentAndUpperRanges,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeClientsDownsellToRangeQuickAction() {
      return {
        quickActionGroupId: `clients-downsell-to-range${this.index}`,
        shortDescription: `Clientes que desceram na faixa ${this.index}`,
        longDescription: `Clientes únicos que realizaram uma compra em alguma faixa superior no período ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e alguma compra na faixa ${this.index} no período ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeClientsDownsellToRange(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              this.skusFromCurrentRange,
              this.skusFromUpperRanges,
              this.skusFromCurrentAndLowerRanges,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeClientBoughtProductQuickAction(product) {
      return {
        quickActionGroupId: `clients-bought-the-product-${product.name}`,
        shortDescription: `Clientes que compraram o produto ${product.name}`,
        longDescription: `Clientes únicos que compraram o produto ${product.name} no período ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup([
            priceRangeClientBoughtProduct(
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              product.sku,
              this.localUnits,
              this.unitsConfiguration
            ),
          ]),
      }
    },
    priceRangeClientUpsellByProductQuickAction(product) {
      return {
        quickActionGroupId: `clients-upsell-to-range-${this.index}-by-product-${product.name}`,
        shortDescription: `Clientes que subiram para a faixa ${this.index} através do produto ${product.name}`,
        longDescription: `Clientes únicos que realizaram uma compra em alguma faixa inferior no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e compraram o produto ${product.name} no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeClientUpsellByProduct(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              product.sku,
              this.skusFromLowerRanges,
              this.skusFromCurrentAndUpperRanges,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeClientDownsellByProductQuickAction(product) {
      return {
        quickActionGroupId: `clients-downsell-to-range-${this.index}-by-product-${product.name}`,
        shortDescription: `Clientes que desceram para a faixa ${this.index} através do produto ${product.name}`,
        longDescription: `Clientes únicos que realizaram uma compra em alguma faixa superior no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e compraram o produto ${product.name} no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeClientDownsellByProduct(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              product.sku,
              this.skusFromUpperRanges,
              this.skusFromCurrentAndLowerRanges,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeNewClientAtRangeQuickAction() {
      return {
        quickActionGroupId: `new-clients-at-range-${this.index}`,
        shortDescription: `Clientes novos na faixa ${this.index}`,
        longDescription: `Clientes únicos que não realizaram uma compra nesta faixa no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e realizaram compra no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeNewClientAtRange(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              this.skusFromCurrentRange,
              this.allSkus,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeLostClientAtRangeQuickAction() {
      return {
        quickActionGroupId: `lost-clients-at-range-${this.index}`,
        shortDescription: `Clientes perdidos na faixa ${this.index}`,
        longDescription: `Clientes únicos que realizaram uma compra nesta faixa no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e não compraram no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeLostClientAtRange(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              this.skusFromCurrentRange,
              this.allSkus,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeNewClientByProductQuickAction(product) {
      return {
        quickActionGroupId: `new-clients-to-range-${this.index}-by-product-${product.name}`,
        shortDescription: `Clientes novos na faixa ${this.index} através do produto ${product.name}`,
        longDescription: `Clientes únicos que não realizaram uma compra deste produto ${product.name} no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e realizaram no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeNewClientByProduct(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              product.sku,
              this.allSkus,
              this.unitsConfiguration
            )
          ),
      }
    },
    priceRangeLostClientByProductQuickAction(product) {
      return {
        quickActionGroupId: `lost-clients-to-range-${this.index}-by-product-${product.name}`,
        shortDescription: `Clientes perdidos na faixa ${this.index} através do produto ${product.name}`,
        longDescription: `Clientes únicos que realizaram uma compra deste produto ${product.name} no período referência ${this.formatedDateTime['reference_period_start']} - ${this.formatedDateTime['reference_period_end']} e não realizaram compra no período atual ${this.formatedDateTime['period_start']} - ${this.formatedDateTime['period_end']}.`,
        segmentationGroup: () =>
          criteriaGroup(
            priceRangeLostClientByProduct(
              this.formatedDateTimeQuickActions['reference_period_start'],
              this.formatedDateTimeQuickActions['reference_period_end'],
              this.formatedDateTimeQuickActions['period_start'],
              this.formatedDateTimeQuickActions['period_end'],
              product.sku,
              this.allSkus,
              this.unitsConfiguration
            )
          ),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dollar-sign-square {
  color: $branco;
  display: inline-block;
  font-size: 22px;
  line-height: 1;
  font-weight: 400;
  border-radius: 4px;
  padding: 12px 24px;
}
.price-range-legend {
  font-size: 18px;
  line-height: 120%;
  display: inline-block;
}

.gray-table-container {
  background-color: $cinza-l90;
  border-radius: 8px;
  &-headers {
    display: grid;
    grid-template-columns: 20% 20% repeat(5, 1fr);
  }
}

.select-range {
  cursor: pointer;
}

.legend-item {
  padding-left: 16px;
  p {
    font-size: 16px;
    color: $cinza-d10;
    font-weight: normal;
  }

  h6 {
    font-weight: normal;
    font-size: 18px;
  }
}

::v-deep th {
  color: white !important;
  font-size: 16px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

::v-deep .v-data-table__wrapper {
  border-radius: 8px;
}

::v-deep .thead-1 {
  background-repeat: repeat;
  background-image: url('../../assets/1-price-range.svg') !important;
}
::v-deep .thead-2 {
  background-repeat: repeat;
  background-image: url('../../assets/2-price-range.svg') !important;
}
::v-deep .thead-3 {
  background-repeat: repeat;
  background-image: url('../../assets/3-price-range.svg') !important;
}
::v-deep .thead-4 {
  background-repeat: repeat;
  background-image: url('../../assets/4-price-range.svg') !important;
}
::v-deep .thead-5 {
  background-repeat: repeat;
  background-image: url('../../assets/5-price-range.svg') !important;
}
::v-deep .thead-6 {
  background-repeat: repeat;
  background-image: url('../../assets/6-price-range.svg') !important;
}
::v-deep .thead-7 {
  background-repeat: repeat;
  background-image: url('../../assets/7-price-range.svg') !important;
}
::v-deep .thead-8 {
  background-repeat: repeat;
  background-image: url('../../assets/8-price-range.svg') !important;
}
::v-deep .thead-9 {
  background-repeat: repeat;
  background-image: url('../../assets/9-price-range.svg') !important;
}
::v-deep .thead-10 {
  border-radius: 10px;
  background-repeat: repeat;
  background-image: url('../../assets/10-price-range.svg') !important;
}
</style>
